import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTicketDetail } from '../../hooks/useSupport';
import { useNotifications } from '../../context/NotificationContext';
import { TICKET_STATUS } from '../../services/supportService';
import { sendStaffReplyNotification } from '../../services/supportService';
import TicketStatusBadge from './TicketStatusBadge';
import TicketPriorityBadge from './TicketPriorityBadge';
import ReplyForm from './ReplyForm';
import InternalNotes from './InternalNotes';
import { toast } from 'react-toastify';
import PresenceDot from '../ui/PresenceDot';

const TicketDetail = ({ userId, ticketId, onBack }) => {
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [isNotifying, setIsNotifying] = useState(false);
  const { 
    ticket, 
    isLoading, 
    isError, 
    error, 
    reply, 
    isReplying, 
    addInternalNote,
    isAddingNote,
    updateStatus, 
    isUpdatingStatus 
  } = useTicketDetail(userId, ticketId);
  
  // Ajouter le hook useNotifications
  const { markTicketAsRead, markTicketReplyNotifsReadForTicket } = useNotifications();

  // Réponse client : considérée comme lue dès l'ouverture du ticket
  useEffect(() => {
    if (userId && ticketId) {
      markTicketReplyNotifsReadForTicket(userId, ticketId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, ticketId]);
  
  // Log pour vérifier quand le composant est monté/démonté
  // Déplacé avant les conditions de retour anticipé
  useEffect(() => {
    console.log(`TicketDetail monté: ${ticketId}`);
    return () => {
      console.log(`TicketDetail démonté: ${ticketId}`);
    };
  }, [ticketId]);
  
  // Marquer automatiquement le ticket comme traité lorsqu'il est ouvert
  useEffect(() => {
    if (ticket && ticket.status === TICKET_STATUS.NEW) {
      markTicketAsRead(ticketId);
    }
  }, [ticket, ticketId, markTicketAsRead]);
  
  // Marquer automatiquement le ticket comme traité lorsque le statut change
  useEffect(() => {
    if (ticket && ticket.status !== TICKET_STATUS.NEW) {
      markTicketAsRead(ticketId);
    }
  }, [ticket?.status, ticketId, markTicketAsRead]);
  
  // État de chargement
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
        <span className="ml-3 text-base-content/70">Chargement du ticket...</span>
      </div>
    );
  }
  
  // État d'erreur
  if (isError) {
    return (
      <div className="bg-error/10 text-error p-4 rounded-md">
        <div className="flex justify-between">
          <h3 className="font-bold">Erreur lors du chargement du ticket</h3>
          <button onClick={onBack} className="btn btn-sm btn-ghost">
            Retour
          </button>
        </div>
        <p>{error?.message || 'Une erreur inconnue est survenue'}</p>
      </div>
    );
  }
  
  // Si le ticket n'est pas trouvé
  if (!ticket) {
    return (
      <div className="p-4 text-center">
        <p className="text-base-content">Ticket non trouvé.</p>
        <button onClick={onBack} className="btn btn-sm mt-2">
          Retour à la liste
        </button>
      </div>
    );
  }
  
  // Formater la date au format lisible
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date instanceof Date ? date : new Date(date));
  };
  
  // Vérifier si le ticket est actif (peut recevoir des réponses)
  const isTicketActive = ![TICKET_STATUS.RESOLVED, TICKET_STATUS.CLOSED].includes(ticket.status);
  
  // Gérer l'envoi d'une réponse
  const handleSendReply = (content) => {
    reply({
      content,
      staffName: 'Admin MSD' // Pourrait être remplacé par le nom de l'admin connecté
    });
    markTicketAsRead(ticketId);
  };

  // Notifier par email (même template que le chat des demandes de création)
  const handleNotifyStaff = async () => {
    setIsNotifying(true);
    try {
      await sendStaffReplyNotification(userId, ticketId);
      toast.success('Email de notification envoyé au client.');
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email de notification:', error);
      toast.error(error?.message || "Impossible d'envoyer l'email.");
    } finally {
      setIsNotifying(false);
    }
  };
  
  // Gérer l'ajout d'une note interne
  const handleAddNote = (content) => {
    // S'assurer que le contenu est correctement passé
    addInternalNote({
      content, // Passer l'objet complet
      adminName: 'Admin MSD' // Pourrait être remplacé par le nom de l'admin connecté
    });
    
    // Suppression de la ligne avec e.preventDefault() car e n'est pas défini
  };
  
  // Gérer la résolution du ticket
  const handleResolve = () => {
    updateStatus(TICKET_STATUS.RESOLVED);
  };
  
  // Gérer la réouverture du ticket
  const handleReopen = () => {
    updateStatus(TICKET_STATUS.IN_PROGRESS);
  };
  
  // Gérer la fermeture définitive du ticket
  const handleClose = () => {
    updateStatus(TICKET_STATUS.CLOSED);
    setShowConfirmClose(false);
  };
  
  return (
    <div>
      {/* En-tête du ticket */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <button 
            onClick={onBack}
            className="btn btn-sm btn-ghost mb-2 pl-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Retour
          </button>
          <h2 className="text-xl font-bold text-base-content">{ticket.title}</h2>
          <div className="flex flex-wrap gap-2 items-center mt-2">
            <TicketStatusBadge status={ticket.status} />
            <TicketPriorityBadge priority={ticket.priority} />
            <span className="text-sm text-base-content/60">
              Créé le {formatDate(ticket.createdAt)}
            </span>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Link
            to={`/clients/${ticket.userId}`}
            className="btn btn-sm btn-outline"
            title="Voir la fiche client consolidée (site, création, support)"
          >
            🗂 Fiche client
          </Link>
          <button
            className="btn btn-sm btn-outline"
            onClick={handleNotifyStaff}
            disabled={isNotifying}
            title="Envoyer au client un email l'informant qu'un message de l'équipe l'attend"
          >
            {isNotifying ? <span className="loading loading-spinner loading-xs"></span> : '📧'} Notifier par email
          </button>
          {isTicketActive ? (
            <button
              className="btn btn-sm btn-success"
              onClick={handleResolve}
              disabled={isUpdatingStatus}
            >
              Résoudre
            </button>
          ) : (
            <button
              className="btn btn-sm btn-warning"
              onClick={handleReopen}
              disabled={isUpdatingStatus || ticket.status === TICKET_STATUS.CLOSED}
            >
              Réouvrir
            </button>
          )}
          
          {ticket.status !== TICKET_STATUS.CLOSED && (
            <button
              className="btn btn-sm btn-error"
              onClick={() => setShowConfirmClose(true)}
              disabled={isUpdatingStatus}
            >
              Fermer
            </button>
          )}
        </div>
      </div>
      
      {/* Informations utilisateur */}
      <div className="bg-base-200/50 p-4 rounded-md mb-6">
        <h3 className="font-medium mb-2 text-base-content">Informations utilisateur</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-base-content/70">Nom</p>
            <p className="flex items-center gap-1.5 text-base-content">
              {ticket.userInfo?.displayName}
              <PresenceDot uid={ticket.userId} />
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-base-content/70">Email</p>
            <p className="text-base-content">{ticket.userInfo?.email}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-base-content/70">ID Utilisateur</p>
            <p className="font-mono text-sm text-base-content/80">{ticket.userId}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-base-content/70">Catégorie</p>
            <p className="text-base-content">{ticket.category || 'Non spécifiée'}</p>
          </div>
        </div>
      </div>
      
      {/* Description du problème */}
      <div className="mb-6">
        <h3 className="font-medium mb-2 text-base-content">Description du problème</h3>
        <div className="bg-base-200/30 p-4 rounded-md whitespace-pre-wrap text-base-content">
          {ticket.description}
        </div>
      </div>
      
      {/* Conversation */}
      <div className="mb-6">
        <h3 className="font-medium mb-2 text-base-content">Conversation</h3>
        {ticket.messages && ticket.messages.length > 0 ? (
          <div className="space-y-4">
            {ticket.messages.map((message, index) => (
              <div 
                key={index}
                className={`p-4 rounded-lg ${
                  message.isStaff 
                    ? 'bg-primary/10 ml-8' 
                    : 'bg-base-200/50 mr-8'
                }`}
              >
                <div className="flex justify-between mb-1">
                  <span className="font-medium text-base-content">
                    {message.isStaff 
                      ? message.authorName || 'Support' 
                      : ticket.userInfo?.displayName || 'Utilisateur'
                    }
                  </span>
                  <span className="text-sm text-base-content/60">
                    {formatDate(message.timestamp)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-base-content">{message.content}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center py-4 text-base-content/60">
            Aucun message dans cette conversation.
          </p>
        )}
      </div>
      
      {/* Formulaire de réponse */}
      {isTicketActive && (
        <ReplyForm 
          onSendReply={handleSendReply}
          isSubmitting={isReplying}
        />
      )}
      
      {/* Notes internes */}
      <InternalNotes
        notes={ticket?.internalNotes || []}
        onAddNote={handleAddNote}
        isSubmitting={isAddingNote}
      />
      
      {/* Boîte de dialogue de confirmation pour fermeture définitive */}
      {showConfirmClose && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="font-bold text-lg mb-2 text-base-content">Confirmer la fermeture</h3>
            <p className="text-base-content">
              Êtes-vous sûr de vouloir fermer définitivement ce ticket ? 
              Cette action ne pourra pas être annulée.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button 
                className="btn btn-outline"
                onClick={() => setShowConfirmClose(false)}
              >
                Annuler
              </button>
              <button 
                className="btn btn-error"
                onClick={handleClose}
              >
                Fermer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail;